import { useState, type ReactNode } from "react";
import { can, type Role } from "@sts/schemas";
import type {
  AnnotatedMatrixPair,
  MatrixClassification,
  Me,
  PackageMeasureView,
  RetrofitGate,
  RetrofitMeasurePackage,
  RetrofitProjectDetail,
  RetrofitReadiness,
  VentilationCategorisation,
} from "../api/types.js";
import { isOnFigureD1 } from "../api/retrofit-types.js";
import { ApiError } from "../api/client.js";
import { Async } from "../components/Async.jsx";
import { usePortal } from "../app/context.js";
import { useAsync } from "../app/use-async.js";
import { href } from "../app/router.js";
import { humanise, longDate } from "../lib/format.js";

/**
 * A PAS 2035 retrofit project.
 *
 * The second compliance regime. Government-funded retrofit — Warm Homes, ECO —
 * runs on PAS 2035 and TrustMark rather than on MCS, and the two are separate
 * lifecycles over separate records: a project may have an MCS job behind it,
 * and just as legitimately may not, because a loft-insulation-only job never
 * touches MCS at all.
 *
 * Three things on this screen are not available anywhere else in this market,
 * and each is here because it is a compliance answer rather than a status:
 * the Figure D.1 verdict on the measures as a set, the Approved Document F
 * categorisation that decides whether ventilation must be upgraded, and the
 * list of gates the API would actually refuse on.
 *
 * The panels load independently rather than together. A surveyor scoped to
 * their own records can hold the project and be refused the organisation-wide
 * reads underneath it, and failing the whole screen on that would make the
 * project unopenable for the role standing in the property.
 */
export function Retrofit({ id }: { readonly id: string }): ReactNode {
  const { client } = usePortal();
  const { state, reload } = useAsync<RetrofitProjectDetail>(
    () => client.retrofitProject(id),
    [client, id],
  );

  return (
    <Async state={state} what="this retrofit project" reload={reload}>
      {(detail) => (
        <>
          <Header detail={detail} />
          <McsJob detail={detail} />
          <Roles detail={detail} />
          <Lodgement detail={detail} />
          <Measures projectId={id} />
          <Readiness projectId={id} />
        </>
      )}
    </Async>
  );
}

/* -------------------------------------------------------------------------- */
/* The project                                                                 */
/* -------------------------------------------------------------------------- */

function Header({
  detail,
}: {
  readonly detail: RetrofitProjectDetail;
}): ReactNode {
  const p = detail.project;
  return (
    <>
      <div className="page-header">
        <p className="small">
          <a href={href.retrofits()}>Retrofit projects</a>
        </p>
        <h1>{p.reference}</h1>
        <p className="lede">
          {humanise(p.stage)} · {humanise(p.funding_scheme)} · PAS 2035:
          {p.pas_version}
          {p.large_scale ? <span className="muted"> · large-scale</span> : null}
        </p>
      </div>

      {p.pas_version === "2019" ? (
        <div className="notice warn" role="note">
          <p className="small">
            This project is recorded against PAS 2035:2019, which was withdrawn
            on 30 March 2025 when PAS 2035:2023 came into effect. That is only
            correct for a project that commenced before then — this one
            commenced {longDate(p.commenced_on)}.
          </p>
        </div>
      ) : null}

      <section>
        <h2>Record</h2>
        <dl className="fields">
          <div className="row">
            <dt>Commenced</dt>
            <dd>{longDate(p.commenced_on)}</dd>
          </div>
          <div className="row">
            <dt>TrustMark project</dt>
            <dd>
              {p.trustmark_project_reference ? (
                <span className="mono">{p.trustmark_project_reference}</span>
              ) : (
                <span className="muted">Not lodged</span>
              )}
            </dd>
          </div>
          <div className="row">
            <dt>Approved design</dt>
            <dd>
              {detail.approvedDesign ? (
                <>
                  Version {detail.approvedDesign.version}, approved{" "}
                  {longDate(detail.approvedDesign.approved_at)}
                </>
              ) : (
                <span className="muted">
                  No design has been approved by the Retrofit Coordinator
                </span>
              )}
            </dd>
          </div>
          <div className="row">
            <dt>Handover</dt>
            <dd>
              {p.handed_over_on ? (
                longDate(p.handed_over_on)
              ) : (
                <span className="muted">Not handed over</span>
              )}
            </dd>
          </div>
          <div className="row">
            <dt>Basic evaluation</dt>
            <dd>
              {p.basic_evaluation_completed_on ? (
                <>Completed {longDate(p.basic_evaluation_completed_on)}</>
              ) : detail.basicEvaluationDueOn ? (
                <>
                  Due {longDate(detail.basicEvaluationDueOn)}{" "}
                  {/* The clock is three calendar months, not ninety days. At the
                      end of a long month those are different dates, and being
                      two days late on a compliance deadline is still late. */}
                  <span className="muted small">
                    three months after handover, per PAS 2035 clause 13.4.3
                  </span>
                </>
              ) : (
                <span className="muted">
                  The three-month clock in clause 13.4.3 starts at handover, and
                  handover has not been recorded
                </span>
              )}
            </dd>
          </div>
        </dl>
      </section>
    </>
  );
}

/**
 * The MCS job behind this project, or the fact that there is not one.
 *
 * The absence is a real answer and gets a sentence rather than a blank field.
 * Most PAS 2035 work never becomes an MCS job — insulation, draught-proofing
 * and glazing have nothing to certify under MCS — so an empty box here would
 * read as missing data on the majority of projects.
 */
function McsJob({
  detail,
}: {
  readonly detail: RetrofitProjectDetail;
}): ReactNode {
  return (
    <section>
      <h2>MCS job</h2>
      {detail.job ? (
        <p>
          <a href={href.job(detail.job.id)}>{detail.job.reference}</a>{" "}
          <span className="muted small">{humanise(detail.job.stage)}</span>
        </p>
      ) : (
        <p className="empty">
          This retrofit project has no MCS job behind it, and does not need one.
          MCS certifies a heat pump, biomass or solar installation; insulation,
          ventilation and draught-proofing measures are certified under PAS 2030
          and lodged to TrustMark instead. A job appears here only once a measure
          on this project is also an MCS installation.
        </p>
      )}
    </section>
  );
}

/**
 * Who is appointed, which is a per-project fact rather than an organisational one.
 *
 * PAS roles travel with the person, not with the company: the same individual
 * is Coordinator on one project and Assessor on the next, and the Coordinator on
 * a local authority scheme frequently works for somebody else entirely.
 */
function Roles({
  detail,
}: {
  readonly detail: RetrofitProjectDetail;
}): ReactNode {
  if (detail.roles.length === 0) {
    return (
      <section>
        <h2>Appointments</h2>
        <p className="empty">
          Nobody has been appointed to this project yet. PAS 2035 roles are held
          per project rather than per organisation, so an appointment recorded
          against the company does not carry here.
        </p>
      </section>
    );
  }

  return (
    <section>
      <h2>Appointments</h2>
      <div className="table-scroll">
        <table>
          <caption className="visually-hidden">
            PAS 2035 appointments on this project, live ones first
          </caption>
          <thead>
            <tr>
              <th scope="col">Role</th>
              <th scope="col">Holder</th>
              <th scope="col">TrustMark licence</th>
              <th scope="col">Appointment</th>
            </tr>
          </thead>
          <tbody>
            {detail.roles.map((role) => (
              <tr key={role.id}>
                <th scope="row">{humanise(role.role)}</th>
                <td>
                  {role.holder ?? (
                    <span className="muted">A user of this system</span>
                  )}
                </td>
                <td>
                  {role.trustmark_licence_number ? (
                    <span className="mono">{role.trustmark_licence_number}</span>
                  ) : (
                    <span className="muted">Not recorded</span>
                  )}
                </td>
                <td>
                  {role.ended_on ? (
                    <span className="muted">
                      Ended {longDate(role.ended_on)}
                    </span>
                  ) : (
                    <>Live since {longDate(role.started_on)}</>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/* The measures package                                                        */
/* -------------------------------------------------------------------------- */

/** Severity classes, reusing the deadline palette rather than adding one. */
const CLASSIFICATION_CLASS: Record<MatrixClassification, string> = {
  incompatible: "status-breached",
  unknown: "status-critical",
  "other-interaction": "status-critical",
  "physical-connection": "status-approaching",
  independent: "status-tracking",
};

function Measures({ projectId }: { readonly projectId: string }): ReactNode {
  const { client } = usePortal();
  const { state, reload } = useAsync<RetrofitMeasurePackage>(
    () => client.retrofitMeasures(projectId),
    [client, projectId],
  );

  return (
    <Async state={state} what="the measures package" reload={reload}>
      {(pkg) => (
        <>
          <MeasureList measures={pkg.measures} />
          <Matrix pkg={pkg} />
          <Ventilation ventilation={pkg.ventilation} />
        </>
      )}
    </Async>
  );
}

function MeasureList({
  measures,
}: {
  readonly measures: readonly PackageMeasureView[];
}): ReactNode {
  return (
    <section>
      <h2>Measures</h2>
      {measures.length === 0 ? (
        <p className="empty">
          No measures have been recorded on this project. Until there are, there
          is nothing to check against the interaction matrix and no package to
          categorise under Approved Document F.
        </p>
      ) : (
        <div className="table-scroll">
          <table>
            <caption>
              Every measure on this project, including any already in the
              dwelling — Approved Document F 3.8 a) and PAS 2035 clause 8.2.13
              NOTE 2 both require existing measures to be counted, because
              airtightness accumulates.
            </caption>
            <thead>
              <tr>
                <th scope="col">Measure</th>
                <th scope="col">Status</th>
                <th scope="col">Ventilation impact</th>
                <th scope="col">Figure D.1 row</th>
                <th scope="col">Evidence</th>
              </tr>
            </thead>
            <tbody>
              {measures.map((measure) => (
                <tr key={measure.id}>
                  <th scope="row">{humanise(measure.category)}</th>
                  <td>
                    {humanise(measure.status)}
                    {measure.counted ? null : (
                      <>
                        <br />
                        <span className="muted small">
                          not counted towards the ventilation category
                        </span>
                      </>
                    )}
                  </td>
                  <td>
                    <AdfImpact measure={measure} />
                  </td>
                  <td>
                    {isOnFigureD1(measure.figureD1) ? (
                      <>
                        <span className="muted small">
                          {measure.figureD1.axis}.
                        </span>{" "}
                        {measure.figureD1.label}
                        {measure.figureD1.note ? (
                          <>
                            <br />
                            <span className="muted small">
                              {measure.figureD1.note}
                            </span>
                          </>
                        ) : null}
                      </>
                    ) : (
                      <span className="muted small">
                        {measure.figureD1.reason}
                      </span>
                    )}
                  </td>
                  <td>
                    {measure.pas_2030_certificate_number ? (
                      <>
                        PAS 2030{" "}
                        <span className="mono small">
                          {measure.pas_2030_certificate_number}
                        </span>
                      </>
                    ) : (
                      <span className="muted small">
                        No PAS 2030 certificate recorded
                      </span>
                    )}
                    {measure.trustmark_lodgement_reference ? (
                      <>
                        <br />
                        Lodged{" "}
                        <span className="mono small">
                          {measure.trustmark_lodgement_reference}
                        </span>
                      </>
                    ) : null}
                    {measure.mcs_job_id ? (
                      <>
                        <br />
                        <a href={href.job(measure.mcs_job_id)}>MCS job</a>
                      </>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

/**
 * A measure's Approved Document F Table 3.1 class.
 *
 * An unclassified Table 3.1 measure is called out rather than shown blank,
 * because a missing classification and a classification of "none" are different
 * facts and the design approval gate refuses on the first.
 */
function AdfImpact({
  measure,
}: {
  readonly measure: PackageMeasureView;
}): ReactNode {
  if (measure.adf_ventilation_impact !== null) {
    return <>{humanise(measure.adf_ventilation_impact)}</>;
  }
  if (measure.in_adf_table_3_1) {
    return (
      <span className="status-word status-critical">
        Not classified
        <span className="muted small">
          {" "}
          · Approved Document F Table 3.1 lists this measure, so somebody has to
          say whether it is minor or major
        </span>
      </span>
    );
  }
  return (
    <span className="muted small">Not listed in Table 3.1</span>
  );
}

/**
 * The Figure D.1 verdict.
 *
 * Annex D is normative and clause 8.2.20 states that a retrofit design shall
 * not include any combination shown red, so this is a compliance gate rather
 * than advice. Three states rather than two: a pair nobody could read is
 * reported as unread, never folded in with the permitted ones.
 */
function Matrix({ pkg }: { readonly pkg: RetrofitMeasurePackage }): ReactNode {
  const { matrix, pairs } = pkg;
  const forbidden = pairs.filter((p) => p.classification === "incompatible");
  const conditional = pairs.filter(
    (p) =>
      p.classification === "physical-connection" ||
      p.classification === "other-interaction",
  );
  const unknown = pairs.filter((p) => p.classification === "unknown");
  const independent = pairs.filter((p) => p.classification === "independent");

  // Whether a combination exists at all, which is not the same as whether one
  // could be read. A lone measure forms no pair even when it is unplaceable, so
  // it gets the empty state; two unplaceable measures form a pair the figure
  // cannot classify, which is a verdict of "not established" rather than
  // nothing to check. Mirrors the `forMatrix.length > 1` test the API applies.
  const inDesign = pkg.measures.filter((m) => m.status !== "cancelled").length;

  return (
    <section>
      <h2>Measures interaction matrix</h2>

      {inDesign < 2 ? (
        <p className="empty">
          There is no combination to check. Clause 8.2.20 is about pairs of
          measures, so a project with fewer than two measures has nothing the
          matrix can forbid.
        </p>
      ) : (
        <>
          {/* `pkg.verdict`, never `matrix.verdict`: the latter cannot know
              about a measure that was never put to it. */}
          <Verdict
            verdict={pkg.verdict}
            pairs={pairs.length}
            uncheckable={pkg.uncheckable.length}
          />

          {forbidden.length > 0 ? (
            <PairTable
              heading="Forbidden combinations"
              caption="Clause 8.2.20 states that the retrofit design shall not include these. There is no design-around and no sign-off route."
              pairs={forbidden}
            />
          ) : null}

          {unknown.length > 0 ? (
            <PairTable
              heading="Pairs this reading could not settle"
              caption="Never treated as permission. Have the Retrofit Designer read each cell off Figure D.1 directly."
              pairs={unknown}
            />
          ) : null}

          {conditional.length > 0 ? (
            <PairTable
              heading="Combinations that attract a duty"
              caption="Permitted, but each places a normative duty on the design before it can proceed."
              pairs={conditional}
            />
          ) : null}

          {independent.length > 0 ? (
            <details>
              <summary>
                {independent.length} pair
                {independent.length === 1 ? "" : "s"} the figure shows as
                independent
              </summary>
              <PairTable
                heading=""
                caption="Reported so that the check is visible, not because anything follows from it."
                pairs={independent}
              />
            </details>
          ) : null}
        </>
      )}

      {pkg.uncheckable.length > 0 ? (
        <div className="notice warn" role="note">
          <p className="small">
            <strong>Measures this check could not include</strong>
          </p>
          <ul className="small">
            {pkg.uncheckable.map((gap) => (
              <li key={gap.category}>
                <strong>{humanise(gap.category)}</strong> — {gap.reason}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <p className="standard">{pkg.editionWarning}</p>
      <p className="standard">
        Transcribed from {pkg.matrix.transcription.figure}. {pkg.matrix.transcription.edition}{" "}
        Read {pkg.matrix.transcription.readOn};{" "}
        {pkg.matrix.transcription.cellsTranscribed} cells transcribed,{" "}
        {pkg.matrix.transcription.cellsUnknown} unreadable.
      </p>
    </section>
  );
}

function Verdict({
  verdict,
  pairs,
  uncheckable,
}: {
  readonly verdict: "compliant" | "non-compliant" | "indeterminate";
  readonly pairs: number;
  readonly uncheckable: number;
}): ReactNode {
  if (verdict === "non-compliant") {
    return (
      <div className="notice fail" role="alert">
        <p>
          <strong>
            This combination of measures does not conform to PAS 2035.
          </strong>{" "}
          Figure D.1 shows at least one pair as incompatible, and clause 8.2.20
          states that the retrofit design shall not include it. Remove one of the
          two measures or substitute a different one — this is a prohibition, not
          a risk to be managed.
        </p>
      </div>
    );
  }
  if (verdict === "indeterminate") {
    return (
      <div className="notice warn" role="alert">
        <p>
          <strong>Not established.</strong> No forbidden pair was found among
          the {pairs} checked, but{" "}
          {uncheckable > 0
            ? `${uncheckable} measure${uncheckable === 1 ? "" : "s"} could not be placed on Figure D.1 at all, so ${uncheckable === 1 ? "its pairs were" : "their pairs were"} never checked`
            : "at least one cell could not be read off Figure D.1"}
          . An unread cell is not a green one: the figure carries 106
          incompatible pairs out of 820, so an unchecked pair has roughly a one
          in eight chance of being a prohibition. This design has not been shown
          to satisfy clause 8.2.20.
        </p>
      </div>
    );
  }
  return (
    <div className="notice" role="status">
      <p>
        All {pairs} pair{pairs === 1 ? "" : "s"} were read off Figure D.1 and
        none is incompatible, so clause 8.2.20 does not stand in the way of this
        design. Any duty attaching to a yellow or orange pair still does.
      </p>
    </div>
  );
}

function PairTable({
  heading,
  caption,
  pairs,
}: {
  readonly heading: string;
  readonly caption: string;
  readonly pairs: readonly AnnotatedMatrixPair[];
}): ReactNode {
  return (
    <>
      {heading ? <h3>{heading}</h3> : null}
      <div className="table-scroll">
        <table>
          <caption>{caption}</caption>
          <thead>
            <tr>
              <th scope="col">Pair</th>
              <th scope="col">Figure D.1 says</th>
              <th scope="col">What the design must do</th>
            </tr>
          </thead>
          <tbody>
            {pairs.map((pair) => (
              <tr key={`${pair.axisA ?? pair.labelA}:${pair.axisB ?? pair.labelB}`}>
                <th scope="row">
                  {pair.labelA}
                  <br />
                  <span className="muted">with</span> {pair.labelB}
                  {pair.categoryA && pair.categoryB ? (
                    <>
                      <br />
                      <span className="muted small">
                        recorded here as {humanise(pair.categoryA)} and{" "}
                        {humanise(pair.categoryB)}
                      </span>
                    </>
                  ) : null}
                </th>
                <td>
                  <span
                    className={`status-word ${CLASSIFICATION_CLASS[pair.classification]}`}
                  >
                    {pair.key.keyText}
                  </span>
                  <br />
                  <span className="muted small">
                    {pair.key.colour === "none"
                      ? "no cell read"
                      : `${pair.key.colour} on the figure`}
                    {" · "}
                    {pair.source.clause}
                  </span>
                  {pair.unknownBecause ? (
                    <>
                      <br />
                      <span className="muted small">{pair.unknownBecause}</span>
                    </>
                  ) : null}
                </td>
                <td>
                  <span className="small">{pair.key.duty}</span>
                  {pair.key.dutyClause ? (
                    <>
                      <br />
                      <span className="muted small">
                        PAS 2035 clause {pair.key.dutyClause}
                      </span>
                    </>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

/* -------------------------------------------------------------------------- */
/* Ventilation                                                                 */
/* -------------------------------------------------------------------------- */

/**
 * The Approved Document F categorisation, and what it costs.
 *
 * The single most load-bearing value in the retrofit package: clause 8.2.13
 * keys the entire ventilation duty off it. Category B or C, or evidence of
 * condensation or mould, means the Retrofit Designer shall confirm the adequacy
 * of the existing ventilation and specify an upgrade where it is inadequate —
 * and the design cannot be approved until somebody has.
 */
function Ventilation({
  ventilation: v,
}: {
  readonly ventilation: VentilationCategorisation;
}): ReactNode {
  return (
    <section>
      <h2>Ventilation</h2>

      <p className="tally">
        <span className="count">{v.majorCount} major</span>{" "}
        <span className="count">{v.minorCount} minor</span>{" "}
        {v.unclassifiedCount > 0 ? (
          <span className="count prov-assumed">
            {v.unclassifiedCount} unclassified
          </span>
        ) : null}{" "}
        <span className="muted small">
          counting measures already in the dwelling
        </span>
      </p>

      <dl className="fields">
        <div className="row">
          <dt>Category</dt>
          <dd>
            {v.category.kind === "category" ? (
              <>
                <strong>Category {v.category.category}</strong>{" "}
                <span className="muted small">
                  read off Approved Document F Diagram 3.1 at{" "}
                  {v.majorCount} major and {v.minorCount} minor
                </span>
              </>
            ) : (
              <>
                <strong>Off the diagram</strong>
                <br />
                <span className="muted small">{v.category.guidance}</span>
              </>
            )}
          </dd>
        </div>
        <div className="row">
          <dt>Condensation or mould</dt>
          <dd>
            {v.condensationOrMouldObserved ? (
              <span className="status-word status-critical">
                Observed by the Retrofit Assessment
                <span className="muted small">
                  {" "}
                  · clause 8.2.13 b) makes this an independent trigger, whatever
                  the measures are
                </span>
              </span>
            ) : (
              <span className="muted">
                Not recorded by a completed Retrofit Assessment
              </span>
            )}
          </dd>
        </div>
        <div className="row">
          <dt>Assessment</dt>
          <dd>
            {v.recorded ? (
              <>
                Category {v.recorded.ventilation_category.toUpperCase()},
                assessed {longDate(v.recorded.assessed_on)} against{" "}
                {v.recorded.assessed_to}
                <br />
                <span className="muted small">
                  Existing provision{" "}
                  {v.recorded.existing_provision_adequate
                    ? "adequate"
                    : "inadequate"}
                  {" · "}
                  proposed strategy{" "}
                  {humanise(v.recorded.proposed_strategy).toLowerCase()}
                  {v.recorded.expert_advice_sought
                    ? " · expert advice sought"
                    : ""}
                </span>
              </>
            ) : (
              <span className="muted">None recorded</span>
            )}
          </dd>
        </div>
      </dl>

      <Blocked ventilation={v} />

      <p className="standard">{v.assumption}</p>
    </section>
  );
}

/**
 * Why design approval is blocked, when it is.
 *
 * Stated as the gate itself states it. The trigger is exact and it is NOT "any
 * insulation measure": clause 8.2.13 fires when the package of specified and
 * existing measures is not Approved Document F category A, or when the
 * assessment found condensation or mould.
 */
function Blocked({
  ventilation: v,
}: {
  readonly ventilation: VentilationCategorisation;
}): ReactNode {
  if (v.unclassifiedCount > 0) {
    return (
      <div className="notice fail" role="alert">
        <p>
          <strong>The package cannot be categorised.</strong>{" "}
          {v.unclassifiedCount} measure
          {v.unclassifiedCount === 1 ? " is" : "s are"} listed in Approved
          Document F Table 3.1 and nobody has classified{" "}
          {v.unclassifiedCount === 1 ? "it" : "them"} as minor or major. Until
          somebody does, Diagram 3.1 cannot be read and there is no way to tell
          whether clause 8.2.13 requires a ventilation assessment. Design
          approval is refused rather than assuming the measures are harmless.
        </p>
      </div>
    );
  }

  const triggeredByCategory = !v.isCategoryA;
  const triggered = triggeredByCategory || v.condensationOrMouldObserved;

  if (!triggered) {
    return (
      <div className="notice" role="status">
        <p>
          The package is Approved Document F category A — no major measures and
          no more than two minor ones — and the assessment recorded no
          condensation or mould, so clause 8.2.13 does not require a ventilation
          assessment before the design is approved.
        </p>
      </div>
    );
  }

  if (v.recorded) {
    return (
      <div className="notice pending" role="status">
        <p>
          Clause 8.2.13 requires the adequacy of the existing ventilation to be
          confirmed here, and an assessment has been recorded, so design approval
          is not blocked on this. What is recorded is that a named person holding
          a project role looked at it and wrote down what they found — whether
          the assessment is any good is a human judgement this system does not
          make.
        </p>
      </div>
    );
  }

  return (
    <div className="notice fail" role="alert">
      <p>
        <strong>Design approval is blocked.</strong>{" "}
        {triggeredByCategory
          ? `The package of ${v.majorCount} major and ${v.minorCount} minor measures, counting those already in the dwelling, is not Approved Document F category A (clause 8.2.13 a)).`
          : "The Retrofit Assessment recorded evidence of condensation or mould growth (clause 8.2.13 b)), which triggers this regardless of what measures are proposed."}{" "}
        PAS 2035 clause 8.2.13 with Annex C.2.1 requires the Retrofit Designer to
        confirm the adequacy of the existing ventilation and specify an upgrade
        where it is inadequate, and no ventilation assessment has been recorded
        against this project.
      </p>
      <p className="small">
        Annex C.1 is why: infiltration and air leakage supply roughly three
        quarters of the ventilation in a typical UK dwelling, so insulating or
        draught-proofing takes that away and the moisture that used to leak out
        condenses on cold surfaces instead.
      </p>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Gaps and blockers                                                           */
/* -------------------------------------------------------------------------- */

/**
 * What this project must have before it can proceed.
 *
 * Every row is the real gate, run and caught by the API — not a preview of it.
 * A screen that re-implemented these in order to show them would eventually
 * disagree with the version that actually refuses, and the one people believe is
 * the one that never says no.
 */
function Readiness({ projectId }: { readonly projectId: string }): ReactNode {
  const { client } = usePortal();
  const { state, reload } = useAsync<RetrofitReadiness>(
    () => client.retrofitReadiness(projectId),
    [client, projectId],
  );

  return (
    <section>
      <h2>Gaps and blockers</h2>
      <Async state={state} what="the readiness checks" reload={reload}>
        {(readiness) => (
          <>
            {readiness.blocked.length === 0 ? (
              <p className="empty">
                Every check this system enforces is satisfied. That is not the
                same as the project being compliant: these are the gates the API
                refuses on, not the whole of PAS 2035.
              </p>
            ) : (
              <p className="lede">
                {readiness.blocked.length} of {readiness.gates.length} checks are
                outstanding.
              </p>
            )}
            <div className="table-scroll">
              <table>
                <caption className="visually-hidden">
                  The gates this project must pass, and what each one blocks
                </caption>
                <thead>
                  <tr>
                    <th scope="col">Requirement</th>
                    <th scope="col">State</th>
                    <th scope="col">Blocks</th>
                    <th scope="col">Authority</th>
                  </tr>
                </thead>
                <tbody>
                  {readiness.gates.map((gate) => (
                    <GateRow key={gate.id} gate={gate} />
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </Async>
    </section>
  );
}

function GateRow({ gate }: { readonly gate: RetrofitGate }): ReactNode {
  return (
    <tr>
      <th scope="row">
        {gate.requirement}
        {gate.detail ? (
          <>
            <br />
            <span className="muted small">{gate.detail}</span>
          </>
        ) : null}
      </th>
      <td>
        <span
          className={`status-word ${gate.satisfied ? "status-tracking" : "status-breached"}`}
        >
          {gate.satisfied ? "Satisfied" : "Outstanding"}
        </span>
      </td>
      <td className="small">{gate.blocks}</td>
      <td>
        {gate.clause ? (
          <span className="small">{gate.clause}</span>
        ) : (
          // A compliance claim without its authority is nagging. Saying that no
          // clause is recorded is honest; inventing one would be worse than the
          // gap, because somebody would stand on it at an assessment.
          <span className="muted small">
            No clause reference is recorded for this check
          </span>
        )}
      </td>
    </tr>
  );
}

/* -------------------------------------------------------------------------- */
/* TrustMark lodgement                                                         */
/* -------------------------------------------------------------------------- */

/**
 * The eight RIAPI operations, exposed as a panel of buttons that reflects the
 * project's current state.
 *
 * The state we show is the schema's own: whether the project has been started
 * (`trustmark_project_reference`) and whether it has been lodged
 * (`certificate_of_lodgement_reference` + `lodged_at`). That's the truth the
 * service layer refuses on
 * (`ProjectNotStartedAtTrustMarkError` / `ProjectNotLodgedError`), so gating
 * the buttons on it here matches the API rather than second-guessing it.
 *
 * Every action is a POST that requires per-call inputs the schema deliberately
 * does not hold (see `retrofit-trustmark.ts`'s per-call identity objects on
 * why). Rather than build a form for each identity field, this panel is a
 * launcher: the button confirms and posts a minimal placeholder — enough to
 * take a request all the way through the wiring — and the API's own
 * validation refusal is the source of truth on what still has to be filled
 * in. That is deliberately narrow: this panel is the connection the platform
 * was missing, not a full lodgement editor, and a form for each of the eight
 * operations is real, separate work not smuggled into this change.
 *
 * Gating:
 *   - `design:update` role gate — designer / technical-supervisor / owner /
 *     admin. Matches the server-side gate exactly.
 *   - "TrustMark not connected" banner — a 503 with `not_configured` from
 *     ANY probe reads as the deployment having no credentials, and every
 *     action button is replaced by the banner. `me()` is fetched anyway so
 *     the role check runs before deciding whether even to try.
 */
function Lodgement({
  detail,
}: {
  readonly detail: RetrofitProjectDetail;
}): ReactNode {
  const { client } = usePortal();
  const { state } = useAsync<{ me: Me }>(
    async () => ({ me: await client.me() }),
    [client],
  );

  return (
    <Async state={state} what="the lodgement panel" reload={() => {}}>
      {({ me }) => <LodgementPanel detail={detail} role={me.role} />}
    </Async>
  );
}

/** Shorthand for the four roles the server-side gate accepts. */
function mayWriteLodgements(role: Role): boolean {
  return can({ role, resource: "design", action: "update" });
}

function LodgementPanel({
  detail,
  role,
}: {
  readonly detail: RetrofitProjectDetail;
  readonly role: Role;
}): ReactNode {
  const project = detail.project;
  const trustmarkStarted = project.trustmark_project_reference !== null;
  const projectLodged = project.lodged_at !== null;
  const mayWrite = mayWriteLodgements(role);

  return (
    <section aria-labelledby="lodgement-heading">
      <h2 id="lodgement-heading">TrustMark lodgement</h2>

      <dl className="fields">
        <div className="row">
          <dt>TrustMark project reference</dt>
          <dd>
            {project.trustmark_project_reference ? (
              <span className="mono">
                {project.trustmark_project_reference}
              </span>
            ) : (
              <span className="muted">
                Not started at TrustMark yet — submit the assessment, then open
                the project.
              </span>
            )}
          </dd>
        </div>
        <div className="row">
          <dt>Certificate of Lodgement</dt>
          <dd>
            {project.certificate_of_lodgement_reference ? (
              <>
                <span className="mono">
                  {project.certificate_of_lodgement_reference}
                </span>
                {project.lodged_at ? (
                  <>
                    {" "}
                    <span className="muted small">
                      lodged {longDate(project.lodged_at)}
                    </span>
                  </>
                ) : null}
              </>
            ) : (
              <span className="muted">
                No certificate held — a measure has to be lodged first.
              </span>
            )}
          </dd>
        </div>
      </dl>

      {!mayWrite ? (
        <p className="notice" role="note">
          Your role ({humanise(role)}) may read the lodgement state but not
          submit to TrustMark. A designer, technical supervisor, owner or
          admin can.
        </p>
      ) : (
        <LodgementActions
          projectId={project.id}
          trustmarkStarted={trustmarkStarted}
          projectLodged={projectLodged}
        />
      )}
    </section>
  );
}

/**
 * The action buttons themselves.
 *
 * State-driven: an operation whose precondition the schema shows unmet is
 * disabled, with the reason. This mirrors — never reimplements — the
 * refusals the service layer will make: a disabled button here is only a
 * hint, and clicking a mistakenly-enabled one still hits the real gate at
 * the API.
 */
function LodgementActions({
  projectId,
  trustmarkStarted,
  projectLodged,
}: {
  readonly projectId: string;
  readonly trustmarkStarted: boolean;
  readonly projectLodged: boolean;
}): ReactNode {
  const { client } = usePortal();
  const [busy, setBusy] = useState<string | null>(null);
  const [message, setMessage] = useState<
    | { readonly kind: "ok"; readonly text: string }
    | { readonly kind: "err"; readonly text: string }
    | { readonly kind: "not-configured" }
    | null
  >(null);

  async function run(
    name: string,
    action: () => Promise<unknown>,
  ): Promise<void> {
    setBusy(name);
    setMessage(null);
    try {
      await action();
      setMessage({
        kind: "ok",
        text: `${name} accepted. Refresh to see the updated references.`,
      });
    } catch (cause) {
      if (cause instanceof ApiError && cause.code === "not_configured") {
        // 503 not_configured from ANY route means the deployment has no
        // TrustMark credentials wired. Replace the whole action strip with
        // a banner rather than re-flashing it per click.
        setMessage({ kind: "not-configured" });
      } else if (cause instanceof ApiError) {
        setMessage({ kind: "err", text: cause.message });
      } else {
        setMessage({
          kind: "err",
          text: cause instanceof Error ? cause.message : String(cause),
        });
      }
    } finally {
      setBusy(null);
    }
  }

  /** Placeholder ids/inputs — see `Lodgement`'s doc comment on why. */
  const PLACEHOLDER = "PLACEHOLDER";
  const placeholderAssessmentId = "00000000-0000-0000-0000-000000000000";

  if (message?.kind === "not-configured") {
    return (
      <div className="notice warn" role="alert">
        <p>
          <strong>TrustMark not connected.</strong> This deployment has no
          TrustMark Retrofit Integration API credentials configured
          (TRUSTMARK_* env vars). Set them and restart to enable lodgement,
          or ask an operator to.
        </p>
      </div>
    );
  }

  const disabledUntilStarted = trustmarkStarted
    ? undefined
    : "The project has not been started at TrustMark yet.";
  const disabledUntilLodged = projectLodged
    ? undefined
    : "The project has no Certificate of Lodgement yet.";

  return (
    <>
      <div className="actions-row" role="group" aria-label="TrustMark actions">
        <ConfirmButton
          label="Submit assessment"
          confirm="Submit the Retrofit Assessment to TrustMark?"
          busy={busy === "Submit assessment"}
          reason={
            trustmarkStarted
              ? "The project has already been started; a further assessment lodgement is unusual."
              : undefined
          }
          onRun={() =>
            run("Submit assessment", () =>
              client.submitAssessmentToTrustmark(projectId, {
                assessmentId: placeholderAssessmentId,
                ownerTMLN: PLACEHOLDER,
              }),
            )
          }
        />
        <ConfirmButton
          label="Start project"
          confirm="Open the project at TrustMark (ProjectStart)?"
          busy={busy === "Start project"}
          reason={
            trustmarkStarted
              ? "The project has already been started."
              : undefined
          }
          onRun={() =>
            run("Start project", () =>
              client.startTrustmarkProject(projectId, {
                assessmentId: placeholderAssessmentId,
                ownerTMLN: PLACEHOLDER,
                schemeId: PLACEHOLDER,
                projectType: PLACEHOLDER,
              }),
            )
          }
        />
        <ConfirmButton
          label="Submit measure lodgement"
          confirm="Lodge the first installed measure against this project?"
          busy={busy === "Submit measure lodgement"}
          reason={disabledUntilStarted}
          onRun={() =>
            run("Submit measure lodgement", () =>
              client.lodgeTrustmarkMeasure(
                projectId,
                placeholderAssessmentId,
                {
                  ownerTMLN: PLACEHOLDER,
                  workCarriedOutBySchemeId: PLACEHOLDER,
                  planStageSequence: 0,
                },
              ),
            )
          }
        />
        <ConfirmButton
          label="Complete project"
          confirm="Close the project with the post-works RdSAP file?"
          busy={busy === "Complete project"}
          reason={disabledUntilLodged}
          onRun={() =>
            run("Complete project", () =>
              client.completeTrustmarkProject(projectId, {
                assessmentId: placeholderAssessmentId,
              }),
            )
          }
        />
        <ConfirmButton
          label="Amend project"
          confirm="Send a corrected post-works assessment (ProjectAmend)?"
          busy={busy === "Amend project"}
          reason={disabledUntilStarted}
          onRun={() =>
            run("Amend project", () =>
              client.amendTrustmarkProject(projectId, {
                assessmentId: placeholderAssessmentId,
              }),
            )
          }
        />
        <ConfirmButton
          label="Amend lodgement"
          confirm="Amend the measures on a previous lodgement?"
          busy={busy === "Amend lodgement"}
          reason={disabledUntilStarted}
          onRun={() =>
            run("Amend lodgement", () =>
              client.amendTrustmarkLodgement(projectId, {
                lodgementId: PLACEHOLDER,
                measures: [{ note: PLACEHOLDER }],
              }),
            )
          }
        />
        <ConfirmButton
          label="Update property information"
          confirm="Update the property information at TrustMark?"
          busy={busy === "Update property information"}
          reason={disabledUntilStarted}
          onRun={() =>
            run("Update property information", () =>
              client.updateTrustmarkPropertyInformation(projectId, {}),
            )
          }
        />
        <ConfirmButton
          label="Void project"
          confirm="Void this project at TrustMark? This cannot be undone from the portal."
          busy={busy === "Void project"}
          reason={disabledUntilStarted}
          destructive
          onRun={() =>
            run("Void project", () => client.voidTrustmarkProject(projectId))
          }
        />
      </div>

      {message?.kind === "ok" ? (
        <p className="notice" role="status">
          {message.text}
        </p>
      ) : null}
      {message?.kind === "err" ? (
        <p className="notice fail" role="alert">
          {message.text}
        </p>
      ) : null}
    </>
  );
}

/**
 * One action button, with a native `confirm()` gate and a small "disabled
 * because" hint.
 *
 * `confirm()` is enough here — the panel is the connection this platform was
 * missing, not a full lodgement editor, and a bespoke dialog would take
 * effort away from the wiring proof itself.
 */
function ConfirmButton({
  label,
  confirm,
  busy,
  reason,
  destructive,
  onRun,
}: {
  readonly label: string;
  readonly confirm: string;
  readonly busy: boolean;
  readonly reason?: string;
  readonly destructive?: boolean;
  readonly onRun: () => void;
}): ReactNode {
  const disabled = busy || reason !== undefined;
  return (
    <span className="lodgement-action">
      <button
        type="button"
        className={destructive ? "danger" : "secondary"}
        disabled={disabled}
        onClick={() => {
          if (globalThis.confirm(confirm)) onRun();
        }}
      >
        {busy ? `${label}…` : label}
      </button>
      {reason ? (
        <>
          {" "}
          <span className="muted small">{reason}</span>
        </>
      ) : null}
    </span>
  );
}
